import { ToggleRightIcon } from '@blocksuite/icons/rc';
import clsx from 'clsx';
import {
  type ForwardedRef,
  forwardRef,
  type KeyboardEvent,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
} from 'react';

import {
  SIDEBAR_ACTION_REVEAL_KEEP_ACTIVE_ATTR,
  SIDEBAR_ACTION_REVEAL_LOCK_EVENT,
  SidebarActionReveal,
} from '../action-reveal';
import * as styles from './index.css';

export const CATEGORY_DIVIDER_ACTION_LOCK_EVENT =
  SIDEBAR_ACTION_REVEAL_LOCK_EVENT;
export const CATEGORY_DIVIDER_KEEP_ACTIONS_ACTIVE_ATTR =
  SIDEBAR_ACTION_REVEAL_KEEP_ACTIVE_ATTR;

export type CategoryDividerProps = PropsWithChildren<
  {
    label: ReactNode;
    className?: string;
    collapsed?: boolean;
    interactiveLabel?: boolean;
    setCollapsed?: (collapsed: boolean) => void;
  } & {
    [key: `data-${string}`]: unknown;
  }
>;

export const CategoryDivider = forwardRef(
  (
    {
      label,
      children,
      className,
      collapsed,
      interactiveLabel = false,
      setCollapsed,
      ...otherProps
    }: CategoryDividerProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const collapsible = collapsed !== undefined;
    const hasActions =
      children !== undefined && children !== null && children !== false;

    const handleClick = useCallback(() => {
      setCollapsed?.(!collapsed);
    }, [collapsed, setCollapsed]);

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        if (
          event.target !== event.currentTarget ||
          !collapsible ||
          (event.key !== 'Enter' && event.key !== ' ')
        ) {
          return;
        }
        event.preventDefault();
        handleClick();
      },
      [collapsible, handleClick]
    );

    return (
      <SidebarActionReveal
        className={clsx(styles.root, className)}
        ref={ref}
        role={collapsible ? 'switch' : undefined}
        aria-checked={collapsible ? !collapsed : undefined}
        tabIndex={collapsible ? 0 : undefined}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        data-collapsed={collapsed}
        data-collapsible={collapsible}
        data-has-actions={hasActions}
        contentClassName={styles.label}
        actions={children}
        {...otherProps}
      >
        {collapsible ? (
          <ToggleRightIcon
            width={16}
            height={16}
            data-testid="category-divider-collapse-button"
            className={styles.collapseIcon}
          />
        ) : null}
        <span
          className={styles.labelText}
          data-sidebar-action-reveal-pointer-inert={
            interactiveLabel ? undefined : true
          }
          title={typeof label === 'string' ? label : undefined}
        >
          {label}
        </span>
      </SidebarActionReveal>
    );
  }
);

CategoryDivider.displayName = 'CategoryDivider';
